import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GTMService } from '@services';
import { InstallationFeaturesStore } from '@store';
import {
  FeatureCommands,
  FeatureNames,
  ServiceBurnerBasedCommands,
  HeatingServiceBurnerBasedProperties,
  ServiceTimeBasedCommands,
  ServiceTimeBasedProperties,
  FeaturePropertyKey,
  Feature,
} from '@vi-iot/client';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EditFeatureValueModalComponent } from '../../../shared/components/edit-feature-value-modal/edit-feature-value-modal.component';
import { EditFeatureValueData } from '../../../shared/models/editFeatureValueData.model';

@Component({
  selector: 'app-maintenance-information-overlay',
  templateUrl: './maintenance-information-overlay.component.html',
  styleUrls: ['./maintenance-information-overlay.component.scss'],
})
export class MaintenanceInformationOverlayComponent implements OnInit, OnDestroy {
  serviceTimeBased$: Observable<Feature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>>;
  serviceBurnerBased: Feature<HeatingServiceBurnerBasedProperties, ServiceBurnerBasedCommands>;
  editFeatureValueDialogRef: MatDialogRef<EditFeatureValueModalComponent>;
  readonly featureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private modalData: EditFeatureValueData;
  private commonModalData = {
    title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
    categoryNameForGTM: 'Maintenance Information',
    errorText: {
      required: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.REQUIRED',
      min: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MIN',
      max: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MAX',
      pattern: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.NUMBERS_PATTERN',
    },
  };

  constructor(
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.serviceTimeBased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.serviceTimeBased
    );

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        HeatingServiceBurnerBasedProperties,
        ServiceBurnerBasedCommands
      >(FeatureNames.serviceBurnerBased)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((serviceBurnerBased) => {
        this.serviceBurnerBased = serviceBurnerBased;
        if (this.serviceBurnerBased) {
          this.modalData = {
            ...this.commonModalData,
            value: Number(this.serviceBurnerBased.properties?.serviceIntervalBurnerHours?.value),
            inputSettings: {
              name:
                '+INSTALLATIONS.INSTALLATION.MAINTENANCE_INFORMATION.BURNER_HOURS_SINCE_LAST_MAINTENANCE.LABEL',
              actionNameForGTM: 'Set hours interval value',
              type: 'number',
              isRequired: this.serviceBurnerBased.commands.setHourInterval.params.hour.required,
              min: this.serviceBurnerBased.commands.setHourInterval.params.hour.constraints.min,
              max: this.serviceBurnerBased.commands.setHourInterval.params.hour.constraints.max,
              pattern: '[0-9]+',
            },
            onSaveProperties: {
              featureName: FeatureNames.serviceBurnerBased,
              propertyName: this.featurePropertyKey.hour,
              paramName: '',
              commandName: this.serviceBurnerBased.commands.setHourInterval.name,
            },
          };
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  public openEditModal(): void {
    this.editFeatureValueDialogRef = this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      data: this.modalData,
    });
    this.gtmService.track(
      'onOpen',
      `Edit ${this.modalData.inputSettings.actionNameForGTM} dialog`,
      this.commonModalData.categoryNameForGTM
    );
  }
}
